import { ReactNode, useEffect, useRef } from 'react';
import { classNames } from 'primereact/utils';
import { Sidebar } from '@/components/sidebar';
import { Titlebar } from '@/components/title-bar';
import { JSXElement } from '@/common/types';
import { useDocumentTitle } from '@/hooks/utils';
import './style.css';
import { useGetUserProperties } from '@/hooks/amplitude/get-user-props';
import { analyticsService } from '@itential/analytics-service';
import { useDrawerState } from '@itential/cloud-portal-sidebar';

interface JobViewerLayoutProps {
    children: ReactNode;
}

export const JobViewerLayout = ({ children }: JobViewerLayoutProps): JSXElement => {
    useDocumentTitle();

    const [isSidebarOpen] = useDrawerState();

    /** Prevents the initial mount calculations from being animated */
    const isInitialRender = useRef(true);

    useEffect(() => {
        isInitialRender.current = false;
    }, [isSidebarOpen]);

    const userInfo = useGetUserProperties();
    const isAmplitudeInitialized = useRef(false);

    useEffect(() => {
        if (!isAmplitudeInitialized.current) {
            analyticsService.init({ userProperties: userInfo });
            isAmplitudeInitialized.current = true;
        }
        analyticsService.setUserProperties(userInfo);
    }, [userInfo]);

    return (
        <div className="layout">
            <header id="job-viewer-titlebar">
                <Titlebar />
            </header>
            <div
                id="job-viewer-sidebar"
                className={classNames({
                    visible: isSidebarOpen,
                    hidden: !isSidebarOpen,
                    animate: !isInitialRender.current,
                })}
            >
                <Sidebar />
            </div>
            <main
                id="job-viewer-main"
                className={classNames({
                    contracted: isSidebarOpen,
                    expanded: !isSidebarOpen,
                    animate: !isInitialRender.current,
                })}
            >
                {children}
            </main>
        </div>
    );
};
